import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/utils/posthog";
import {
  FOUR_GEARS as TIERS,
  GEAR_COUNT as TIER_COUNT,
  gearRgba as rgba,
  type Gear as Tier,
} from "@/data/fourGears";

// Pyramid geometry (pure math — safe to compute at module scope for SSR). A
// triangle with its apex at top-center and base along the bottom, sliced into
// four equal-height horizontal bands with a small gap between them.
const W = 720;
const H = 460;
const CX = W / 2;
const ROW_H = H / TIER_COUNT;
const GAP = 16;

// Half-width of the triangle at vertical position y (0 = apex, H = base).
const halfWidth = (y: number) => (W / 2) * (y / H);

interface Band extends Tier {
  i: number;
  points: string;
  labelY: number;
  fontSize: number;
}

// Precompute each band's polygon + label position. `i` is the position from the
// bottom (0 = base), matching the TIERS index and the reveal order.
const BANDS: Band[] = TIERS.map((tier, i) => {
  const isApex = i === TIER_COUNT - 1;
  const isBase = i === 0;
  const yTop = (TIER_COUNT - 1 - i) * ROW_H;
  const yBot = (TIER_COUNT - i) * ROW_H;
  // Inset the shared edges to leave a clean gap between neighbouring bands, but
  // keep the apex point sharp and the base edge flat.
  const dTop = yTop + (isApex ? 0 : GAP / 2);
  const dBot = yBot - (isBase ? 0 : GAP / 2);

  const points = isApex
    ? `${CX},${dTop} ${CX + halfWidth(dBot)},${dBot} ${CX - halfWidth(dBot)},${dBot}`
    : `${CX - halfWidth(dTop)},${dTop} ${CX + halfWidth(dTop)},${dTop} ${CX + halfWidth(dBot)},${dBot} ${CX - halfWidth(dBot)},${dBot}`;

  // Nudge the apex number toward the wider lower portion of its triangle.
  const labelY = isApex ? dTop + (dBot - dTop) * 0.62 : (dTop + dBot) / 2;

  return { ...tier, i, points, labelY, fontSize: isApex ? 30 : 36 };
});

// The annotated list mirrors the pyramid spatially: apex (Loops) on top, base
// (Chat) at the bottom, so a row lights up in step with its band.
const TIERS_TOP_DOWN: Band[] = [...BANDS].reverse();

const SVG_PAD = 12;

/**
 * Presentation-ready reveal of the "4 Gears of AI Usage" pyramid.
 *
 * SSR / no-JS: renders the COMPLETE model — all four tiers and labels visible —
 * because `mounted` starts false. The progressive hiding (and all browser-API
 * wiring: keydown, IntersectionObserver, matchMedia) is a client-only
 * enhancement applied after mount, so the prerendered HTML always ships the full
 * model and there is no hydration mismatch.
 */
const FourGearsReveal = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inView, setInView] = useState(false);

  const countRef = useRef(0);
  const firedRef = useRef(false);

  const advance = useCallback(
    () => setRevealedCount((c) => Math.min(TIER_COUNT, c + 1)),
    [],
  );
  const back = useCallback(
    () => setRevealedCount((c) => Math.max(0, c - 1)),
    [],
  );

  // Flip on the client-only enhancement once, after hydration.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Keep a ref of the current count for the keydown handler (avoids re-binding
  // the listener on every advance).
  useEffect(() => {
    countRef.current = revealedCount;
  }, [revealedCount]);

  // Honour prefers-reduced-motion.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Only treat keys as reveal controls while the section is actually on screen,
  // so arrows don't advance a hidden reveal from another slide.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Fire the completion event once, when every tier is shown.
  useEffect(() => {
    if (mounted && revealedCount >= TIER_COUNT && !firedRef.current) {
      firedRef.current = true;
      trackEvent("AI Mastermind 4 Gears Revealed");
    }
  }, [mounted, revealedCount]);

  // Keyboard controls, gated to when the section is in view. ArrowRight / Space
  // advance, ArrowLeft goes back. ArrowUp/ArrowDown and the scroll wheel are left
  // alone so the surrounding scroll deck keeps working.
  useEffect(() => {
    if (!inView || typeof window === "undefined") return;
    const onKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      const editable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        !!active?.isContentEditable;
      if (editable) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        advance();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      } else if (e.key === " " || e.key === "Spacebar") {
        // Let a focused button/link handle its own Space activation, and let
        // Space fall through to scroll once the reveal is complete.
        if (tag === "BUTTON" || tag === "A") return;
        if (countRef.current >= TIER_COUNT) return;
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inView, advance, back]);

  // A tier is shown when JS hasn't taken over yet (SSR / no-JS: show everything)
  // or when it falls within the revealed range.
  const revealStyle = (i: number, dy: number): CSSProperties => {
    const shown = !mounted || i < revealedCount;
    return {
      opacity: shown ? 1 : 0,
      transform: shown || reducedMotion ? "none" : `translateY(${dy}px)`,
      transition: reducedMotion
        ? "opacity 150ms linear"
        : "opacity 550ms ease, transform 550ms ease",
    };
  };

  const complete = revealedCount === TIER_COUNT;
  const showControls = mounted;
  const showHint = mounted && !complete;
  const latest = revealedCount > 0 ? TIERS[revealedCount - 1] : null;

  return (
    <section
      ref={sectionRef}
      className="min-h-screen flex items-center px-6 py-24 border-t border-white/10"
    >
      <div className="max-w-6xl mx-auto w-full">
        {/* 03 · One Move to Try eyebrow — keeps the deck coherent */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6">
          <span className="text-2xl sm:text-3xl font-bold text-white/40 tabular-nums">
            03
          </span>
          <span className="text-white/30 text-2xl sm:text-3xl">·</span>
          <span className="text-white/80 text-2xl sm:text-3xl font-semibold tracking-tight">
            One Move to Try
          </span>
        </div>

        <h2 className="text-white text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] drop-shadow-lg">
          4 Gears of AI Usage
        </h2>
        <p className="mt-4 text-white/70 text-lg sm:text-xl lg:text-2xl font-medium tracking-tight max-w-2xl drop-shadow">
          Each gear is meant for a different terrain of problem to solve. Which
          gears are you comfortable with?
        </p>

        {/* Reveal stage — clicking/tapping anywhere here advances */}
        <div
          onClick={advance}
          className={cn(
            "mt-10 sm:mt-14 grid items-center gap-10 lg:gap-16 lg:grid-cols-2 select-none",
            showHint ? "cursor-pointer" : "cursor-default",
          )}
        >
          <div className="mx-auto w-full max-w-[420px] sm:max-w-[480px] drop-shadow-xl">
            <svg
              viewBox={`${-SVG_PAD} ${-SVG_PAD} ${W + SVG_PAD * 2} ${H + SVG_PAD * 2}`}
              className="w-full h-auto"
              role="img"
              aria-label="4 Gears of AI Usage pyramid, from base to apex: 1 Chat, 2 Assistant, 3 Agents, 4 Loops."
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {/* Dark halo so the wireframe stays legible wherever it crosses
                    the busy photographic background behind the section. */}
                <filter
                  id="gears-legibility"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx={0}
                    dy={0}
                    stdDeviation={7}
                    floodColor="#000"
                    floodOpacity={0.75}
                  />
                </filter>
                {/* Soft backing that calms the background directly behind the
                    pyramid so it reads as one solid object, not stray lines. */}
                <radialGradient id="gears-backing" cx="50%" cy="58%" r="62%">
                  <stop offset="0%" stopColor="#000" stopOpacity={0.55} />
                  <stop offset="60%" stopColor="#000" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#000" stopOpacity={0} />
                </radialGradient>
              </defs>

              {/* Backing scrim — decorative, sits behind the whole pyramid. */}
              <rect
                x={-SVG_PAD}
                y={-SVG_PAD}
                width={W + SVG_PAD * 2}
                height={H + SVG_PAD * 2}
                fill="url(#gears-backing)"
                aria-hidden="true"
              />

              {/* Ghost frame — always visible so the pyramid reads as a solid,
                  four-tier shape even in its default (unrevealed) state. A faint
                  fill, a stronger stroke and the dark halo keep it clear against
                  the dark photo; the reveal then lights each band in colour. */}
              <g aria-hidden="true" filter="url(#gears-legibility)">
                {BANDS.map((band) => (
                  <polygon
                    key={`frame-${band.name}`}
                    points={band.points}
                    fill="rgba(255,255,255,0.05)"
                    stroke="rgba(255,255,255,0.34)"
                    strokeWidth={2}
                    strokeLinejoin="round"
                  />
                ))}
              </g>

              {/* Resting tier numbers — faint on load so the pyramid announces
                  "four gears" before anything is revealed. Each revealed band
                  paints a full-accent number directly on top of these. */}
              <g aria-hidden="true" filter="url(#gears-legibility)">
                {BANDS.map((band) => (
                  <text
                    key={`ghost-num-${band.name}`}
                    x={CX}
                    y={band.labelY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="rgba(255,255,255,0.3)"
                    style={{
                      fontFamily: "inherit",
                      fontWeight: 700,
                      fontSize: band.fontSize,
                    }}
                  >
                    {band.n}
                  </text>
                ))}
              </g>

              {/* Revealed bands */}
              {BANDS.map((band) => (
                <g
                  key={band.name}
                  style={revealStyle(band.i, 14)}
                  aria-hidden="true"
                >
                  <polygon
                    points={band.points}
                    fill={rgba(band.accent, 0.22)}
                    stroke={rgba(band.accent, 0.6)}
                    strokeWidth={2}
                    strokeLinejoin="round"
                  />
                  <text
                    x={CX}
                    y={band.labelY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={band.accent}
                    style={{
                      fontFamily: "inherit",
                      fontWeight: 700,
                      fontSize: band.fontSize,
                    }}
                  >
                    {band.n}
                  </text>
                </g>
              ))}
            </svg>
          </div>

          {/* Annotated tiers — apex (Loops) on top, base (Chat) at the bottom */}
          <ul className="space-y-4 sm:space-y-5">
            {TIERS_TOP_DOWN.map((tier) => (
              <li
                key={tier.name}
                style={revealStyle(tier.i, 10)}
                className="flex items-start gap-4 sm:gap-5"
              >
                <span
                  className="mt-0.5 flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg text-lg sm:text-xl font-bold tabular-nums"
                  style={{
                    color: tier.accent,
                    backgroundColor: rgba(tier.accent, 0.14),
                    border: `1px solid ${rgba(tier.accent, 0.4)}`,
                  }}
                >
                  {tier.n}
                </span>
                <div>
                  <div className="text-white text-xl sm:text-2xl font-semibold tracking-tight drop-shadow">
                    {tier.name}
                  </div>
                  {/* Two plain-text lines per gear — "When" to reach for it and
                      "How" to do it — under matching accent labels tied back to
                      the pyramid band colour. The product name inside the How line
                      is a deep link to the exact hands-on step on /claude-masterclass.
                      Static JSX inside the row, so it reveals in step with the gear
                      and ships in the prerendered / no-JS HTML alongside the rest of
                      the row. */}
                  <div className="mt-2 space-y-1.5 text-sm sm:text-base text-white/60 font-medium leading-snug drop-shadow">
                    <div className="flex gap-2">
                      <span
                        className="w-12 shrink-0 font-semibold"
                        style={{ color: tier.accent }}
                      >
                        When
                      </span>
                      <span>{tier.when}</span>
                    </div>
                    <div className="flex gap-2">
                      <span
                        className="w-12 shrink-0 font-semibold"
                        style={{ color: tier.accent }}
                      >
                        How
                      </span>
                      <span>
                        {tier.how.before}
                        <Link
                          to={tier.how.href}
                          onClick={(e) => {
                            e.stopPropagation();
                            trackEvent("AI Mastermind Technique Pill Clicked", {
                              gear: tier.name,
                              technique: tier.how.product,
                            });
                          }}
                          className="font-semibold underline-offset-2 hover:underline"
                          style={{ color: tier.accent }}
                        >
                          {tier.how.product}
                        </Link>
                        {tier.how.after}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Announce the current tier to assistive tech */}
        <div className="sr-only" role="status" aria-live="polite">
          {mounted && latest
            ? `Gear ${latest.n} of ${TIER_COUNT}: ${latest.name}. When to use it: ${latest.when} How to use it: ${latest.how.before}${latest.how.product}${latest.how.after}`
            : ""}
        </div>

        {/* Controls + progress (client-only enhancement) */}
        {showControls && (
          <div className="mt-12 sm:mt-16 flex flex-col items-start gap-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={back}
                disabled={revealedCount === 0}
                aria-label="Reveal previous gear"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="hidden sm:inline">Prev</span>
              </button>
              <button
                type="button"
                onClick={advance}
                disabled={complete}
                aria-label="Reveal next gear"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span className="hidden sm:inline">Next</span>
                <ArrowRight className="h-5 w-5" />
              </button>

              {/* Progress indicator: one dot per tier, filled as revealed (0–4) */}
              <div
                className="ml-2 flex items-center gap-2"
                aria-hidden="true"
              >
                {TIERS.map((tier, i) => (
                  <span
                    key={tier.name}
                    className={cn(
                      "h-2.5 w-2.5 rounded-full transition-colors",
                      i < revealedCount ? "bg-white" : "bg-white/25",
                    )}
                  />
                ))}
              </div>
              <span className="ml-1 text-sm font-semibold tabular-nums text-white/70">
                {revealedCount} / {TIER_COUNT}
              </span>
            </div>

            {showHint && (
              <p className="text-sm font-medium text-white/50">
                Click, or press <span className="text-white/80">&rarr;</span> to
                reveal each gear &middot;{" "}
                <span className="text-white/80">&larr;</span> to go back
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default FourGearsReveal;
